/**
 * Persistent level/XP chip — visible from every authenticated surface
 * (mounted in AppShell next to UsageChip). Shows the player's XP level + a
 * thin progress arc toward the next level. Orthogonal to UsageChip: that's
 * the billing meter, this is the forward-progress meter.
 *
 * Renders null until the gamification query resolves (and stays hidden
 * before the user has earned any XP, so a brand-new account isn't shown a
 * "Level 1, 0 XP" chip before they've done anything).
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ICON_STROKE, Zap } from "@/shared/components/icons";
import { getGamification } from "@/features/gamification/lib/gamification";

export type LevelChipVariant = "sidebar" | "header";

export function LevelChip({
  variant = "sidebar",
}: {
  variant?: LevelChipVariant;
} = {}) {
  const { data } = useQuery({
    queryKey: ["gamification"],
    queryFn: getGamification,
    staleTime: 60_000,
  });

  // Hide until there's something to show.
  if (!data || data.totalXp <= 0) return null;

  const pct =
    data.levelSpan > 0
      ? Math.min(100, Math.round((data.intoLevel / data.levelSpan) * 100))
      : 0;

  const layoutClass =
    variant === "sidebar"
      ? "flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm"
      : "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]";

  const dialSize = variant === "sidebar" ? 18 : 14;
  const ariaLabel = `Level ${data.level} · ${data.totalXp.toLocaleString()} XP`;

  return (
    <Link
      to="/skills/level"
      title={ariaLabel}
      aria-label={ariaLabel}
      className={[
        layoutClass,
        "border border-border bg-bg/40 transition hover:border-accent/60",
      ].join(" ")}
    >
      <span className="relative inline-flex items-center justify-center text-accent">
        <RadialFill percent={pct} size={dialSize} />
        <Zap
          size={dialSize === 18 ? 9 : 7}
          strokeWidth={ICON_STROKE}
          className="absolute"
        />
      </span>
      <span className="truncate">
        <span className="text-text font-medium">Lv {data.level}</span>
        {variant === "sidebar" && (
          <span className="text-text-muted"> · {data.totalXp.toLocaleString()} XP</span>
        )}
      </span>
    </Link>
  );
}

function RadialFill({ percent, size = 14 }: { percent: number; size?: number }) {
  const stroke = size >= 18 ? 2.5 : 2.25;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (clampPercent(percent) / 100) * circumference;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="text-accent"
      aria-hidden
    >
      <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeOpacity={0.22} strokeWidth={stroke} />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeDasharray={`${filled} ${circumference}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}
      />
    </svg>
  );
}

function clampPercent(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 100;
  return n;
}
